import React, {useCallback, useEffect, useRef, useState} from 'react';
import useLookUpBase from "../../components/selectbox/LookupBase";
import IReservationEquipement from "../../models/IReservationEquipement";
import {Popup, Position} from "devextreme-react/popup";
import Guid from "../../models/Guid";
import Form, {FormRef, Label, SimpleItem} from "devextreme-react/form";
import GestionTypesEquipements from "../gestion-types-equipements/gestion-types-equipements";
import {ICustomRule} from "../../hooks/useCustomDataSource";
import useApi, {IApiErreur} from "../../hooks/useApi";

interface IPopupEquipementProps {
  equipement?: IReservationEquipement,
  idUnite: Guid,
  isVisible: boolean;
  togglePopup: () => void,
  onValueChanged: () => void,
  isModeModifier: boolean
}

const PopupEquipement = ({ equipement, isVisible, togglePopup, idUnite, onValueChanged, isModeModifier }: IPopupEquipementProps) => {
  const { editorOptions: equipementEditorOptions, PopupGestion, refreshData } = useLookUpBase({ isModeModifier: isModeModifier, controllerName: `unite/${idUnite}/lookUpEquipements`, cacheKey: "lookup-typeequipement", path: "", showClear: isModeModifier });
  const { fetchData } = useApi<IReservationEquipement>()
  const [validationRules, setValidationRules] = useState<ICustomRule[]>([]);
  const formRef = useRef<FormRef>(null);

  useEffect(() => {
    fetchData("get", { customPath: `reservationDetailEquipement/getValidation`, returnOnly: true }).then((result: string[]) => {
      if (result) {
        setValidationRules(result.map(fieldName => {
          return { type: "async", message: "", reevaluate: true, propertyName: fieldName } as ICustomRule
        }))
      }
    })
  }, [])

  const validate = useCallback(async (data: IReservationEquipement | undefined) => {
    const result = await fetchData("post", {
      customPath: `reservationDetailEquipement/ValidateModel`,
      body: data as unknown as object,
      returnOnly: true
    });
    return result as unknown[] as IApiErreur[];
  }, [fetchData]);

  const validationCallback = useCallback((e: any) => {
    return new Promise(async (resolve, reject) => {
      if (!isModeModifier) return resolve(undefined);
      try {
        const formData = formRef.current?.instance().option('formData');
        const result: IApiErreur[] = await validate({ ...formData, [e.rule.propertyName]: e.value });
        const erreur = result.find(f => f.propertyName.toLowerCase() === e.formItem.dataField.toLowerCase())
        if (erreur) {
          reject(erreur.errorMessage);
        }
      } catch (ex) {
        resolve(undefined);
      }
      resolve(undefined);
    })
  }, [validate, isModeModifier])

  const customizeItem = useCallback((item: any) => {
    if (!isModeModifier || !validationRules || validationRules.length === 0 || !item.dataField) return
    const rule = validationRules.find(f => f.propertyName.toLowerCase() === item.dataField.toLowerCase() || item.dataField.toLowerCase().includes(`.${f.propertyName.toLowerCase()}`))//{type: "async", message: "", validationCallback, reevaluate: true} as unknown as ICustomRule
    if (rule) {
      rule.validationCallback = validationCallback;
      item.validationRules = [rule];
    }
  }, [validationRules, isModeModifier, validationCallback])

  useEffect(() => {
    if (formRef) {
      formRef.current?.instance().option("customizeItem", customizeItem);
    }
  }, [formRef, validationRules, formRef])

  function isValidationErreur(arg: any[]): arg is IApiErreur[] {
    const result = arg && arg.length > 0 && arg[0] && arg[0].propertyName && typeof (arg[0].propertyName) === 'string'
    return result;
  }

  useEffect(() => {
    if (isVisible) {
      refreshData().then(() =>
        formRef.current?.instance().validate())
    }
  }, [isVisible]);


  return (

    <Popup dragEnabled={true}
      visible={true}
      hideOnOutsideClick={true}
      showTitle={false}
      height={"auto"}
      width={"325px"}
      onHiding={async (e) => {
        const result = formRef.current?.instance().validate();
        if (result?.complete) {
          e.cancel = true;
          result?.complete?.then(value => {
            if (value.isValid) {
              togglePopup()
            }
          })
        } else
          togglePopup()
      }}
      shading={true}
    >
      <Position my={"left top"} at={"left bottom"} of={".btn-equipement"} />
      <Form ref={formRef} formData={equipement} readOnly={!isModeModifier} labelLocation={"left"} onFieldDataChanged={() => {
        onValueChanged()
      }}>
        <SimpleItem dataField={"idUniteEquipementType"}
          editorType={"dxSelectBox"}
          editorOptions={equipementEditorOptions}
        >
          <Label text={"Type d'équipement"} />
        </SimpleItem>
        <SimpleItem dataField={"longeurEquipement"} editorType={"dxNumberBox"}>
          <Label text={"Longueur"} />
        </SimpleItem>
      </Form>
      <PopupGestion>
        <GestionTypesEquipements />
      </PopupGestion>
    </Popup>
  )
}

export default PopupEquipement;